import React, { useContext, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, CircularProgress } from '@mui/material';
import { CloseWhiteIcon, TickBigIcon, UploadIcon } from '../../../../assets/icons/icon';
import { LanguageContext } from '../../../../context/LanguageContext';
import { ParagraphText } from '../../../../Libraries';
import { analytics, triggerLogEvent } from '../../../../config/firebase';
import styles from "./FileUpload.module.css";
import { sentenceCase } from '../../../../constants';

const FileUpload = ({ file, setFile, analyzeData, reset }) => {
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const progressIntervalRef = useRef(null);
    const [fileTemp, setTempFile] = useState(null);
    const [I18n] = useContext(LanguageContext);
    const [errorMessage, setErrorMessage] = useState('');


    const { getRootProps, getInputProps } = useDropzone({
        accept: {
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'text/csv': ['.csv']
        },
        multiple: false,
        onDrop: (acceptedFiles, rejectedFiles) => {
            if (rejectedFiles.length > 0) {
                setErrorMessage(I18n.invalid_file_format_csv_xls || 'Invalid file format. Please upload a .csv, .xls, or .xlsx file.');
                return;
            }
            const selectedFile = acceptedFiles[0];
            triggerLogEvent(analytics, "sc_stock_bulk_file_upload", {
                file_name: selectedFile?.name,
                file_type: selectedFile?.type,
                file_size: selectedFile?.size,
            });
            setTempFile(selectedFile)
            setUploading(true);
            setErrorMessage('');
            simulateUploadProgress(selectedFile);
        },
    });


    const simulateUploadProgress = (file) => {
        progressIntervalRef.current = setInterval(() => {
            setProgress((prev) => {
                if (prev >= 100) {
                    clearInterval(progressIntervalRef.current);
                    setUploading(false);
                    setFile(file);
                    analyzeData(file);
                    return 100;
                }
                return prev + 10;
            });
        }, 500);
    };

    const removeFile = () => {
        clearInterval(progressIntervalRef.current);
        setFile(null);
        setTempFile(null);
        setUploading(false);
        setProgress(0);
        setErrorMessage('');
        reset()
    };

    return (
        <Box
            {...getRootProps()}
            className={styles.dropZone}
        >
            <input {...getInputProps()} />

            {file && (
                <Box
                    onClick={(e) => {
                        removeFile()

                        e.stopPropagation();
                    }}
                    className={styles.removeFile}
                >
                    <CloseWhiteIcon />
                </Box>
            )}


            <Box className={styles.contentLeft} >
                {uploading ? (
                    <CircularProgress
                        size={42}
                        sx={{
                            color: 'var(--newLightColor)',
                        }}
                        variant="determinate"
                        value={progress}
                        className={styles.uploadingProgress}
                    />
                ) : fileTemp ? (
                    <TickBigIcon />
                ) : (
                    <UploadIcon />
                )}


                <Box className={styles.textBlock}>
                    {fileTemp ? (
                        <>
                            <ParagraphText className={styles.fileNameText}>
                                {uploading ? fileTemp.name : `${I18n.file_uploaded || "File uploaded"}, "${fileTemp.name}".`}
                            </ParagraphText>
                        </>
                    ) : (
                        <Box className={styles.placeholderBlock}>
                            <ParagraphText>
                                {I18n.select_file_or_drag}
                            </ParagraphText>
                            <ParagraphText className={`fontSize12 ${errorMessage ? styles.subTextError : styles.subText}`}>
                                {errorMessage ? errorMessage : (I18n.csv_xls_files_supported || "(.csv and .xls files are supported)")}
                            </ParagraphText>
                        </Box>
                    )}
                </Box>
            </Box>
            {(uploading || fileTemp) && (
                <ParagraphText className={styles.uploadingText}>
                    {uploading ? `${sentenceCase(I18n.uploading)}...` : sentenceCase(I18n.finished || "Finished")}
                </ParagraphText>
            )}
        </Box>
    );
};

export default FileUpload;
